import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import PurchaseService from '@/services/PurchaseService';
import { supabase } from '@/integrations/supabase/client';

/**
 * Component that automatically synchronizes RevenueCat user identification
 * with the logged-in Supabase user. This ensures purchases are not made anonymously.
 * 
 * With the new approach, RevenueCat is ONLY configured when a Supabase user is authenticated,
 * preventing any anonymous ID creation as per RevenueCat documentation.
 * 
 * This component doesn't render anything, it just manages the synchronization logic.
 */
export default function RevenueCatAuthSync() {
  const { isAuthenticated, user, loading } = useAuth();
  const lastSyncTimeRef = useRef<number>(0);
  const syncInProgressRef = useRef<boolean>(false);
  const rcConfiguredUserIdRef = useRef<string | null>(null);

  // Sync RevenueCat with Supabase auth status - with debounce/throttle
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      
      return;
    }

    // CRITICAL: Don't attempt RevenueCat sync while Supabase auth is still loading
    if (loading) {
      
      return;
    }

    // Prevent too frequent sync attempts - throttle to once per 30 seconds max
    const now = Date.now();
    if (now - lastSyncTimeRef.current < 30000 || syncInProgressRef.current) {
      return;
    }

    const syncAuth = async () => {
      // Set sync in progress flag to prevent simultaneous attempts
      if (syncInProgressRef.current) {
        return;
      }
      
      // Double-check that auth loading is complete before proceeding
      if (loading) {
        
        return;
      }
      
      syncInProgressRef.current = true;
      lastSyncTimeRef.current = now;
      
      try {
        if (isAuthenticated && user?.id) {
          
          
          // Check if we've already configured RevenueCat for this user
          if (rcConfiguredUserIdRef.current === user.id) {
            
            syncInProgressRef.current = false;
            return;
          }
          
          // Ensure RevenueCat is configured/logged in with this user ID
          const success = await PurchaseService.ensureRcLogin();
          
          if (success) {
            
            rcConfiguredUserIdRef.current = user.id;
          } else {
            
          }
        } else {
          
          
          // User logged out - reset RevenueCat state
          if (rcConfiguredUserIdRef.current) {
            
            await PurchaseService.logoutFromRevenueCat();
            rcConfiguredUserIdRef.current = null;
          }
        }
      } catch (error) {
        
      } finally {
        syncInProgressRef.current = false;
      }
    };
    
    // Run sync if authenticated state changed and loading is complete
    syncAuth();
    
    // Set up auth state change listener - but use a relatively long-lived one instead of reconnecting often
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Only act on specific important events
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          
          
          // Reset state on sign out
          if (event === 'SIGNED_OUT') {
            rcConfiguredUserIdRef.current = null;
          }
          
          // Wait a moment to let auth state settle, then check if loading is complete
          setTimeout(() => {
            // Only sync if auth loading is complete
            if (!loading) {
              syncAuth();
            } else {
              
            }
          }, 1000);
        }
      }
    );
    
    // Clean up on unmount
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [isAuthenticated, user?.id, loading]); // Added loading to dependencies

  // No UI to render
  return null;
} 